import React, { useState, useEffect, useRef } from 'react';
import './VideoTimeline.css';

function VideoTimeline({ 
  videoRef, 
  currentTime, 
  duration, 
  onSeek,
  timelineItems = []
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [localTime, setLocalTime] = useState(currentTime);
  const timelineRef = useRef(null);

  useEffect(() => {
    if (!isDragging) {
      setLocalTime(currentTime);
    }
  }, [currentTime, isDragging]);

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleTimelineClick = (e) => {
    if (!timelineRef.current || !duration) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const clickRatio = Math.max(0, Math.min(1, clickX / timelineWidth));
    const newTime = clickRatio * duration;
    
    setLocalTime(newTime);
    onSeek(newTime);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleTimelineClick(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !timelineRef.current || !duration) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const timelineWidth = rect.width;
    const ratio = Math.max(0, Math.min(1, mouseX / timelineWidth));
    const newTime = ratio * duration;
    
    setLocalTime(newTime);
    onSeek(newTime);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, duration]);

  if (!duration) {
    return null;
  }

  const progressPercentage = (localTime / duration) * 100;

  return (
    <div className="video-timeline-container">
      <div className="video-timeline-header">
        <span className="time-current">{formatTime(localTime)}</span>
        <span className="time-total">{formatTime(duration)}</span>
      </div>
      
      <div 
        className="video-timeline-track"
        ref={timelineRef}
        onMouseDown={handleMouseDown}
      >
        {/* Timeline items visualization */}
        {timelineItems.map((item) => {
          if (!item || !duration) return null;
          
          const startTime = item.startTime || item.timestamp || 0;
          const startPercent = Math.max(0, Math.min(100, (startTime / duration) * 100));
          let widthPercent = 2; // Default width for frames
          
          if (item.type === 'clip' && item.endTime && item.endTime > startTime) {
            const clipDuration = item.endTime - startTime;
            widthPercent = Math.max(2, (clipDuration / duration) * 100);
          }
          
          return (
            <div
              key={item.id}
              className={`timeline-marker ${item.type}`}
              style={{
                left: `${startPercent}%`,
                width: item.type === 'frame' ? '3px' : `${widthPercent}%`
              }}
              title={item.type === 'clip' 
                ? `Clip: ${formatTime(startTime)} - ${formatTime(item.endTime || startTime)}`
                : `Frame: ${formatTime(startTime)}`
              }
            />
          );
        }).filter(Boolean)}
        
        {/* Progress bar */}
        <div className="timeline-progress-bg">
          <div 
            className="timeline-progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Playhead */}
        <div 
          className="timeline-playhead"
          style={{ left: `${progressPercentage}%` }}
        />
      </div>
    </div>
  );
}

export default VideoTimeline;